import { useState, useEffect } from 'react';
import { useRateTiers } from '../hooks/useRateTiers';
import { apiGet, apiPost, apiPut } from '../services/api';
import useAuthStore from '../context/authStore';
import toast from 'react-hot-toast';

const EHR_OPTIONS = [
  'PointClickCare',
  'MatrixCare',
  'Netsmart',
  'American HealthTech (AHT)',
  'ECP',
  'Eldermark',
  'ALIS (Medtelligent)',
  'Yardi',
  'EPIC',
  'Cerner',
];

const DEFAULT_POSITION_ROW = () => ({
  key: `pos-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  position: '',
  department: '',
  description: '',
  requiredQualifications: [],
  requiredCertifications: [],
  startDate: '',
  endDate: '',
  hours: '',
  duration: 'one_time',
  shiftType: 'flexible',
  location: { facility: '', address: '', city: '', state: '', zip: '' },
  reportingContact: { name: '', title: '', phone: '', email: '' },
  numberOfWorkersNeeded: 1,
});

export function StaffingRequestModal({
  isOpen,
  onClose,
  onSave,
  editingRequest = null,
  organizationId,
}) {
  const { tiers, loading: tiersLoading } = useRateTiers(organizationId);
  const { user } = useAuthStore();
  const isFacilityUser = ['facility_admin', 'facility_manager', 'facility_worker'].includes(user?.role);
  // Facilities this person may request staff for (facility users get only their own)
  const [facilities, setFacilities] = useState([]);
  useEffect(() => {
    if (!isOpen) return;
    apiGet('/organizations/me/facilities')
      .then((res) => setFacilities(res?.facilities || []))
      .catch(() => setFacilities([]));
  }, [isOpen]);
  const getBlankForm = () => ({
    facilityId: '',
    position: '',
    positions: [DEFAULT_POSITION_ROW()],
    jobTitle: '',
    wageTierId: '',
    wageCalculationLocation: '',
    baseHourlyRate: '',
    calculatedHourlyRate: '',
    department: '',
    frequency: 'daily',
    urgency: 'medium',
    onboardingRequirements: {
      ehrSystems: [],
      ehrOther: '',
      dressCodeStandardScrubs: false,
      dressColor: '',
      stratelBadgeRequired: false,
    },
    billingAuthorization: {
      authorized: false,
      authorizerName: '',
      authorizerTitle: '',
      authorizationDate: '',
      authorizationReference: '',
      notes: '',
    },
    billingParty: {
      type: 'organization',
      organizationId: organizationId || '',
      facilityId: '',
      name: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      },
      apEmail: '',
      apPhone: '',
      notes: '',
    },
    numberOfWorkersNeeded: 1,
    notes: '',
  });
  const [formData, setFormData] = useState(getBlankForm);
  const [expandedPositionKey, setExpandedPositionKey] = useState('');

  useEffect(() => {
    if (editingRequest) {
      const nextForm = {
        ...editingRequest,
        facilityId: editingRequest.facilityId?._id || editingRequest.facilityId || '',
        billingParty: {
          type: editingRequest.billingParty?.type || 'organization',
          organizationId: editingRequest.billingParty?.organizationId || organizationId || '',
          facilityId: editingRequest.billingParty?.facilityId || '',
          name: editingRequest.billingParty?.name || '',
          contactName: editingRequest.billingParty?.contactName || '',
          contactEmail: editingRequest.billingParty?.contactEmail || '',
          contactPhone: editingRequest.billingParty?.contactPhone || '',
          address: {
            street: editingRequest.billingParty?.address?.street || '',
            city: editingRequest.billingParty?.address?.city || '',
            state: editingRequest.billingParty?.address?.state || '',
            zip: editingRequest.billingParty?.address?.zip || '',
            country: editingRequest.billingParty?.address?.country || 'US',
          },
          apEmail: editingRequest.billingParty?.apEmail || '',
          apPhone: editingRequest.billingParty?.apPhone || '',
          notes: editingRequest.billingParty?.notes || '',
        },
        positions:
          Array.isArray(editingRequest.positions) && editingRequest.positions.length > 0
            ? editingRequest.positions
            : [{
                key: 'pos-1',
                position: editingRequest.position || '',
                department: editingRequest.department || '',
                description: editingRequest.description || '',
                requiredQualifications: editingRequest.requiredQualifications || [],
                requiredCertifications: editingRequest.requiredCertifications || [],
                startDate: editingRequest.startDate || '',
                endDate: editingRequest.endDate || '',
                hours: editingRequest.hours || '',
                duration: editingRequest.duration || 'one_time',
                shiftType: editingRequest.shiftType || 'flexible',
                location: editingRequest.location || { facility: '', address: '', city: '', state: '', zip: '' },
                reportingContact: { name: '', title: '', phone: '', email: '' },
                numberOfWorkersNeeded: Math.max(1, Number(editingRequest.numberOfWorkersNeeded || 1)),
              }],
      };
      setFormData(nextForm);
      setExpandedPositionKey(nextForm.positions?.[0]?.key || '');
    } else {
      resetForm();
    }
  }, [editingRequest, isOpen]);

  const resetForm = () => {
    const next = getBlankForm();
    setFormData(next);
    setExpandedPositionKey(next.positions?.[0]?.key || '');
  };

  const handleSelectWageTier = (tierId) => {
    const selectedTier = tiers.find((t) => t._id === tierId);
    if (selectedTier) {
      setFormData({
        ...formData,
        wageTierId: tierId,
        jobTitle: selectedTier.jobTitle,
        baseHourlyRate: selectedTier.baseHourlyRate,
        positions: (formData.positions || []).map((row) => ({
          ...row,
          requiredCertifications: selectedTier.requiredCertifications || row.requiredCertifications || [],
        })),
      });
    }
  };

  const handleLocationChange = (location) => {
    setFormData({ ...formData, wageCalculationLocation: location });

    if (formData.wageTierId && location) {
      const selectedTier = tiers.find((t) => t._id === formData.wageTierId);
      if (selectedTier) {
        const calculatedRate = selectedTier.baseHourlyRate;
        const adjustment = selectedTier.locationAdjustments?.find(
          (a) => a.location.toLowerCase() === location.toLowerCase()
        );

        if (adjustment) {
          const finalRate = calculatedRate * (1 + adjustment.adjustmentPercentage / 100);
          setFormData({ ...formData, wageCalculationLocation: location, calculatedHourlyRate: finalRate });
        } else {
          setFormData({ ...formData, wageCalculationLocation: location, calculatedHourlyRate: calculatedRate });
        }
      }
    }
  };

  const updatePositionRow = (key, field, value) => {
    setFormData((prev) => ({
      ...prev,
      positions: (prev.positions || []).map((row) => row.key === key ? { ...row, [field]: value } : row),
    }));
  };

  const addPositionRow = () => {
    const nextRow = DEFAULT_POSITION_ROW();
    setFormData((prev) => ({
      ...prev,
      positions: [...(prev.positions || []), nextRow],
    }));
    setExpandedPositionKey(nextRow.key);
  };

  const removePositionRow = (key) => {
    setFormData((prev) => {
      const next = (prev.positions || []).filter((row) => row.key !== key);
      const fallback = next.length ? next : [DEFAULT_POSITION_ROW()];
      setExpandedPositionKey((current) => (current === key ? fallback[0]?.key || '' : current));
      return {
        ...prev,
        positions: fallback,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const cleanedPositions = (formData.positions || [])
        .map((row) => ({
          ...row,
          position: String(row.position || '').trim(),
          department: String(row.department || '').trim(),
          numberOfWorkersNeeded: Math.max(1, parseInt(row.numberOfWorkersNeeded, 10) || 1),
        }))
        .filter((row) => row.position);
      if (!cleanedPositions.length) {
        throw new Error('At least one position row is required');
      }
      if (isFacilityUser && facilities.length > 1 && !formData.facilityId) {
        throw new Error('Choose which facility this request is for');
      }
      const invalidRow = cleanedPositions.find(
        (row) =>
          !row.startDate ||
          !row.shiftType ||
          !String(row.location?.facility || '').trim() ||
          !String(row.reportingContact?.name || '').trim() ||
          (!String(row.reportingContact?.phone || '').trim() &&
            !String(row.reportingContact?.email || '').trim())
      );
      if (invalidRow) {
        throw new Error(
          `Each position requires a start date, shift type, facility, report-to name, and either a phone or email for arrival contact`
        );
      }
      if (
        ['facility', 'custom'].includes(formData.billingParty?.type) &&
        !String(formData.billingParty?.name || '').trim()
      ) {
        throw new Error('Billing responsibility requires a bill-to entity name for facility or custom billing');
      }

      const payload = {
        ...formData,
        positions: cleanedPositions,
        position: cleanedPositions[0].position,
        department: cleanedPositions[0].department,
        numberOfWorkersNeeded: cleanedPositions.reduce((sum, row) => sum + row.numberOfWorkersNeeded, 0),
        baseHourlyRate: formData.baseHourlyRate ? parseFloat(formData.baseHourlyRate) : undefined,
        calculatedHourlyRate: formData.calculatedHourlyRate
          ? parseFloat(formData.calculatedHourlyRate)
          : undefined,
      };

      if (editingRequest) {
        await apiPut(`/staffing-requests/${editingRequest._id}`, payload);
        toast.success('Staffing request updated');
      } else {
        await apiPost('/staffing-requests', payload);
        toast.success('Staffing request created');
      }

      onSave();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save staffing request');
    }
  };

  if (!isOpen) return null;

  const toggleEhrSystem = (name) => {
    const selected = formData.onboardingRequirements?.ehrSystems || [];
    const next = selected.includes(name)
      ? selected.filter((item) => item !== name)
      : [...selected, name];
    setFormData({
      ...formData,
      onboardingRequirements: {
        ...(formData.onboardingRequirements || {}),
        ehrSystems: next,
      },
    });
  };

  const setBillingParty = (updater) => {
    setFormData((prev) => ({
      ...prev,
      billingParty:
        typeof updater === 'function'
          ? updater(prev.billingParty || {})
          : { ...(prev.billingParty || {}), ...updater },
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 pt-20 pb-6 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[calc(100vh-6rem)] overflow-y-auto">
        <div className="sticky top-0 bg-gray-100 border-b p-4 flex justify-between items-center z-10">
          <h2 className="text-lg font-bold">
            {editingRequest ? 'Edit Staffing Request' : 'New Staffing Request'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 text-xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {facilities.length > 0 && (
            <div className="border-b pb-6">
              <label htmlFor="staffing-request-facility" className="block text-sm font-medium text-gray-700 mb-2">
                Facility{isFacilityUser && facilities.length > 1 ? ' *' : ''}
              </label>
              {isFacilityUser && facilities.length === 1 ? (
                <p id="staffing-request-facility" className="text-sm text-gray-700">{facilities[0].name}</p>
              ) : (
                <select
                  id="staffing-request-facility"
                  value={formData.facilityId || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, facilityId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{isFacilityUser ? 'Select a facility' : 'Whole organization (no specific facility)'}</option>
                  {facilities.map((facility) => (
                    <option key={facility._id} value={facility._id}>{facility.name}</option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Only people who work at this facility, and your organization admins, can see this request.
              </p>
            </div>
          )}
          {/* Rate Tier Selection */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-semibold mb-4">Rate Configuration</h3>

            {tiersLoading ? (
              <div className="text-gray-500">Loading available rate tiers...</div>
            ) : tiers.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-yellow-800">
                No rate tiers configured. Please contact your administrator to set up job titles and rates.
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Job Title / Rate Tier *
                  </label>
                  <select
                    value={formData.wageTierId}
                    onChange={(e) => handleSelectWageTier(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select a job title --</option>
                    {tiers.map((tier) => (
                      <option key={tier._id} value={tier._id}>
                        {tier.jobTitle} (${tier.baseHourlyRate.toFixed(2)}/hr)
                      </option>
                    ))}
                  </select>
                </div>

                {formData.wageTierId && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Base Hourly Rate
                        </label>
                        <input
                          type="text"
                          value={`$${parseFloat(formData.baseHourlyRate).toFixed(2) || '0.00'}`}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Rate Calculation Location
                        </label>
                        <input
                          type="text"
                          value={formData.wageCalculationLocation}
                          onChange={(e) => handleLocationChange(e.target.value)}
                          placeholder="e.g., New York City"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {formData.calculatedHourlyRate && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700">Calculated Hourly Rate:</span>
                          <span className="text-2xl font-bold text-blue-600">
                            ${parseFloat(formData.calculatedHourlyRate).toFixed(2)}/hr
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Position Details */}
          <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Requested Positions</h3>
                <button
                  type="button"
                  onClick={addPositionRow}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium"
              >
                Add Position
              </button>
            </div>

            {(formData.positions || []).map((row, index) => (
              <div key={row.key} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setExpandedPositionKey((current) => (current === row.key ? '' : row.key))}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="font-medium text-gray-900">
                      {row.position || `Position ${index + 1}`}
                    </div>
                    <div className="text-sm text-gray-500">
                      {[row.department, `${Math.max(1, Number(row.numberOfWorkersNeeded || 1))} worker${Math.max(1, Number(row.numberOfWorkersNeeded || 1)) === 1 ? '' : 's'}`, row.startDate || 'Start date required', row.shiftType ? `${row.shiftType} shift` : 'Shift required']
                        .filter(Boolean)
                        .join(' • ')}
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setExpandedPositionKey((current) => (current === row.key ? '' : row.key))}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {expandedPositionKey === row.key ? 'Collapse' : 'Expand'}
                    </button>
                    <button
                      type="button"
                      onClick={() => removePositionRow(row.key)}
                      className="text-red-600 hover:bg-red-50 rounded px-2 py-1 text-sm"
                      title={`Remove position ${index + 1}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {expandedPositionKey === row.key && (
                  <div className="grid grid-cols-12 gap-4 items-end p-4">
                <div className="col-span-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position Title *
                  </label>
                  <input
                    type="text"
                    value={row.position}
                    onChange={(e) => updatePositionRow(row.key, 'position', e.target.value)}
                    required
                    placeholder="e.g. RN, LPN, CNA"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department
                  </label>
                  <input
                    type="text"
                    value={row.department}
                    onChange={(e) => updatePositionRow(row.key, 'department', e.target.value)}
                    placeholder="e.g. ICU, ER, Med-Surg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Workers
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={row.numberOfWorkersNeeded}
                    onChange={(e) => updatePositionRow(row.key, 'numberOfWorkersNeeded', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-12">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={row.description || ''}
                    onChange={(e) => updatePositionRow(row.key, 'description', e.target.value)}
                    placeholder="Role details, responsibilities, unit-specific context..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="2"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={row.startDate || ''}
                    onChange={(e) => updatePositionRow(row.key, 'startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={row.endDate || ''}
                    onChange={(e) => updatePositionRow(row.key, 'endDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hours per Week
                  </label>
                  <input
                    type="number"
                    value={row.hours || ''}
                    onChange={(e) => updatePositionRow(row.key, 'hours', e.target.value)}
                    placeholder="40"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration Type
                  </label>
                  <select
                    value={row.duration || 'one_time'}
                    onChange={(e) => updatePositionRow(row.key, 'duration', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="one_time">One-time</option>
                    <option value="recurring">Recurring</option>
                    <option value="ongoing">Ongoing</option>
                  </select>
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shift Type *
                  </label>
                  <select
                    value={row.shiftType || 'flexible'}
                    onChange={(e) => updatePositionRow(row.key, 'shiftType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="flexible">Flexible</option>
                    <option value="day">Day Shift</option>
                    <option value="evening">Evening</option>
                    <option value="night">Night</option>
                  </select>
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facility *
                  </label>
                  <input
                    type="text"
                    value={row.location?.facility || ''}
                    onChange={(e) => updatePositionRow(row.key, 'location', { ...(row.location || {}), facility: e.target.value })}
                    placeholder="Facility / unit location"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={row.location?.address || ''}
                    onChange={(e) => updatePositionRow(row.key, 'location', { ...(row.location || {}), address: e.target.value })}
                    placeholder="Address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={row.location?.city || ''}
                    onChange={(e) => updatePositionRow(row.key, 'location', { ...(row.location || {}), city: e.target.value })}
                    placeholder="City"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={row.location?.state || ''}
                    onChange={(e) => updatePositionRow(row.key, 'location', { ...(row.location || {}), state: e.target.value })}
                    placeholder="State"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={row.location?.zip || ''}
                    onChange={(e) => updatePositionRow(row.key, 'location', { ...(row.location || {}), zip: e.target.value })}
                    placeholder="ZIP"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Required Qualifications
                  </label>
                  <input
                    type="text"
                    value={(row.requiredQualifications || []).join(', ')}
                    onChange={(e) => updatePositionRow(row.key, 'requiredQualifications', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                    placeholder="Experience, unit familiarity, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Required Certifications
                  </label>
                  <input
                    type="text"
                    value={(row.requiredCertifications || []).join(', ')}
                    onChange={(e) => updatePositionRow(row.key, 'requiredCertifications', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                    placeholder="BLS, ACLS, CPR"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-12 border-t border-gray-100 pt-3">
                  <h4 className="font-medium mb-3">Report To / Arrival Contact</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={row.reportingContact?.name || ''}
                      onChange={(e) => updatePositionRow(row.key, 'reportingContact', { ...(row.reportingContact || {}), name: e.target.value })}
                      placeholder="Supervisor name *"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={row.reportingContact?.title || ''}
                      onChange={(e) => updatePositionRow(row.key, 'reportingContact', { ...(row.reportingContact || {}), title: e.target.value })}
                      placeholder="Supervisor title"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={row.reportingContact?.phone || ''}
                      onChange={(e) => updatePositionRow(row.key, 'reportingContact', { ...(row.reportingContact || {}), phone: e.target.value })}
                      placeholder="Arrival phone *"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="email"
                      value={row.reportingContact?.email || ''}
                      onChange={(e) => updatePositionRow(row.key, 'reportingContact', { ...(row.reportingContact || {}), email: e.target.value })}
                      placeholder="Arrival email"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Provide the supervisor name and at least one arrival contact method.</p>
                </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Request Urgency
              </label>
              <select
                value={formData.urgency}
                onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex items-end justify-end">
              <p className="text-sm text-gray-600">
                Total workers requested:{' '}
                <span className="font-semibold text-gray-900">
                  {(formData.positions || []).reduce(
                    (sum, row) => sum + Math.max(1, parseInt(row.numberOfWorkersNeeded, 10) || 1),
                    0
                  )}
                </span>
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Onboarding Requirements</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">EHR/eMAR System</label>
              <div className="flex flex-wrap gap-3">
                {EHR_OPTIONS.map((option) => (
                  <label key={option} className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={(formData.onboardingRequirements?.ehrSystems || []).includes(option)}
                      onChange={() => toggleEhrSystem(option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              <input
                type="text"
                value={formData.onboardingRequirements?.ehrOther || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    onboardingRequirements: {
                      ...(formData.onboardingRequirements || {}),
                      ehrOther: e.target.value,
                    },
                  })
                }
                placeholder="Other EHR"
                className="w-full mt-3 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(formData.onboardingRequirements?.dressCodeStandardScrubs)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      onboardingRequirements: {
                        ...(formData.onboardingRequirements || {}),
                        dressCodeStandardScrubs: e.target.checked,
                      },
                    })
                  }
                />
                <span>Standard scrubs</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(formData.onboardingRequirements?.stratelBadgeRequired)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      onboardingRequirements: {
                        ...(formData.onboardingRequirements || {}),
                        stratelBadgeRequired: e.target.checked,
                      },
                    })
                  }
                />
                <span>Stratel badge required</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dress Color</label>
              <input
                type="text"
                value={formData.onboardingRequirements?.dressColor || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    onboardingRequirements: {
                      ...(formData.onboardingRequirements || {}),
                      dressColor: e.target.value,
                    },
                  })
                }
                placeholder="e.g., Navy blue"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Billing Authorization</h3>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={Boolean(formData.billingAuthorization?.authorized)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    billingAuthorization: {
                      ...(formData.billingAuthorization || {}),
                      authorized: e.target.checked,
                    },
                  })
                }
              />
              <span>Billing authorization confirmed</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Authorizer Name</label>
                <input
                  type="text"
                  value={formData.billingAuthorization?.authorizerName || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      billingAuthorization: {
                        ...(formData.billingAuthorization || {}),
                        authorizerName: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Name approving billing"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Authorizer Title</label>
                <input
                  type="text"
                  value={formData.billingAuthorization?.authorizerTitle || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      billingAuthorization: {
                        ...(formData.billingAuthorization || {}),
                        authorizerTitle: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., DON, Finance Manager"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Authorization Date</label>
                <input
                  type="date"
                  value={formData.billingAuthorization?.authorizationDate || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      billingAuthorization: {
                        ...(formData.billingAuthorization || {}),
                        authorizationDate: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO / Charge Authorization / Cost Center #</label>
                <input
                  type="text"
                  value={formData.billingAuthorization?.authorizationReference || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      billingAuthorization: {
                        ...(formData.billingAuthorization || {}),
                        authorizationReference: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Reference number"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Billing Notes</label>
              <textarea
                value={formData.billingAuthorization?.notes || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    billingAuthorization: {
                      ...(formData.billingAuthorization || {}),
                      notes: e.target.value,
                    },
                  })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any authorization or billing routing notes..."
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Billing Responsibility</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who should be billed?
              </label>
              <select
                value={formData.billingParty?.type || 'organization'}
                onChange={(e) => {
                  const type = e.target.value;
                  // Billing a facility defaults to the facility this request is for
                  const defaultFacility = type === 'facility'
                    ? facilities.find((facility) => facility._id === formData.facilityId) || (facilities.length === 1 ? facilities[0] : null)
                    : null;
                  setBillingParty((current) => ({
                    ...current,
                    type,
                    organizationId: organizationId || current.organizationId || '',
                    facilityId: type === 'facility' ? defaultFacility?._id || '' : '',
                    name: type === 'facility' ? defaultFacility?.name || '' : type === 'custom' ? current.name || '' : '',
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="organization">Bill headquarters organization</option>
                <option value="facility">Bill a specific facility</option>
                <option value="custom">Bill another AP / billing entity</option>
              </select>
            </div>

            {formData.billingParty?.type === 'organization' && (
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                The requesting organization will be treated as the default bill-to party unless you provide a different AP contact below.
              </div>
            )}

            {formData.billingParty?.type === 'facility' && facilities.length > 0 && (
              <div>
                <label htmlFor="staffing-billing-facility" className="block text-sm font-medium text-gray-700 mb-2">
                  Facility to bill
                </label>
                <select
                  id="staffing-billing-facility"
                  value={formData.billingParty?.facilityId?._id || formData.billingParty?.facilityId || ''}
                  onChange={(e) => {
                    const facility = facilities.find((item) => item._id === e.target.value);
                    setBillingParty({ facilityId: facility?._id || '', name: facility?.name || '' });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a facility</option>
                  {facilities.map((facility) => (
                    <option key={facility._id} value={facility._id}>{facility.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">The invoice goes to this facility, and its billing users can see it.</p>
              </div>
            )}

            {(formData.billingParty?.type === 'custom' || (formData.billingParty?.type === 'facility' && facilities.length === 0)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bill-To Entity Name
                </label>
                <input
                  type="text"
                  value={formData.billingParty?.name || ''}
                  onChange={(e) => setBillingParty({ name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    formData.billingParty?.type === 'facility'
                      ? 'Facility name'
                      : 'Billing company / AP entity'
                  }
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Contact Name</label>
                <input
                  type="text"
                  value={formData.billingParty?.contactName || ''}
                  onChange={(e) => setBillingParty({ contactName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="AP / billing contact"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Contact Phone</label>
                <input
                  type="text"
                  value={formData.billingParty?.contactPhone || ''}
                  onChange={(e) => setBillingParty({ contactPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Billing phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Contact Email</label>
                <input
                  type="email"
                  value={formData.billingParty?.contactEmail || ''}
                  onChange={(e) => setBillingParty({ contactEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="billing@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AP Email</label>
                <input
                  type="email"
                  value={formData.billingParty?.apEmail || ''}
                  onChange={(e) => setBillingParty({ apEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ap@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                <input
                  type="text"
                  value={formData.billingParty?.address?.street || ''}
                  onChange={(e) =>
                    setBillingParty((current) => ({
                      ...current,
                      address: { ...(current.address || {}), street: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Billing street address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AP Phone</label>
                <input
                  type="text"
                  value={formData.billingParty?.apPhone || ''}
                  onChange={(e) => setBillingParty({ apPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Accounts payable phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={formData.billingParty?.address?.city || ''}
                  onChange={(e) =>
                    setBillingParty((current) => ({
                      ...current,
                      address: { ...(current.address || {}), city: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  value={formData.billingParty?.address?.state || ''}
                  onChange={(e) =>
                    setBillingParty((current) => ({
                      ...current,
                      address: { ...(current.address || {}), state: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="State"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ZIP</label>
                <input
                  type="text"
                  value={formData.billingParty?.address?.zip || ''}
                  onChange={(e) =>
                    setBillingParty((current) => ({
                      ...current,
                      address: { ...(current.address || {}), zip: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ZIP"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Billing Notes</label>
              <textarea
                value={formData.billingParty?.notes || ''}
                onChange={(e) => setBillingParty({ notes: e.target.value })}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Special billing routing, parent-company billing, AP instructions..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 justify-end border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              {editingRequest ? 'Update' : 'Create'} Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
